import { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text as RNText,
  View,
} from 'react-native';

import { BackChevron } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { Pill, type PillTone } from '@/components/Pill';
import { Screen } from '@/components/Screen';
import { Stack } from '@/components/Stack';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, spacing } from '@/constants/theme';
import { formatRelative } from '@/lib/relativeTime';
import { cashoutsService } from '@/services/cashouts';
import { CASHOUT_STATUS, type CashOut, type CashoutStatus } from '@/types/cashout';

// Maps our DB-truth status to a user-facing badge. We never show "claimed" —
// Tremendous exposes no redemption signal, so "Sent" means delivered, not
// necessarily redeemed (see docs/PAYOUTS.md).
const STATUS_PILL: Record<CashoutStatus, { tone: PillTone; label: string }> = {
  [CASHOUT_STATUS.Ready]: { tone: 'green', label: 'Sent' },
  [CASHOUT_STATUS.Failed]: { tone: 'coral', label: 'Refunded' },
  [CASHOUT_STATUS.Requested]: { tone: 'greenSoft', label: 'Pending' },
  [CASHOUT_STATUS.Processing]: { tone: 'greenSoft', label: 'Pending' },
};

export default function CashoutHistory() {
  const [cashOuts, setCashOuts] = useState<CashOut[] | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const { cash_outs } = await cashoutsService.list();
      setCashOuts(cash_outs);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Screen edges={['top', 'bottom']} testID="cashout-history">
      <Stack gap="lg">
        <View style={styles.topBar}>
          <BackChevron />
          <RNText style={styles.title}>Cash Out History</RNText>
          <View style={styles.titleSpacer} />
        </View>

        {cashOuts === null && !error && (
          <View style={styles.center} testID="cashout-history-loading">
            <ActivityIndicator size="large" color={colors.deepGreen} />
          </View>
        )}

        {error && (
          <Card style={styles.stateCard}>
            <Text variant="body" color="textMuted" align="center">
              Couldn&apos;t load your cashouts.
            </Text>
            <Button
              label="Try again"
              variant="outline"
              size="sm"
              onPress={() => void load()}
              accessibilityLabel="Retry loading cash out history"
              testID="cashout-history-retry"
            />
          </Card>
        )}

        {cashOuts?.length === 0 && (
          <Card style={styles.stateCard}>
            <Text variant="caption" color="textMuted" align="center">
              No cashouts yet. When you cash out, it&apos;ll show up here.
            </Text>
          </Card>
        )}

        {cashOuts?.map((cashOut) => (
          <CashOutRow key={cashOut.id} cashOut={cashOut} />
        ))}
      </Stack>
    </Screen>
  );
}

interface CashOutRowProps {
  cashOut: CashOut;
}

function CashOutRow({ cashOut }: CashOutRowProps) {
  // Fallback keeps the row well-formed if the server ever returns a status
  // the client doesn't know yet (rather than rendering a badge-less row).
  const badge = STATUS_PILL[cashOut.status] ?? { tone: 'greenSoft' as PillTone, label: 'Pending' };
  const isFailed = cashOut.status === CASHOUT_STATUS.Failed;

  return (
    <Card style={styles.row} testID={`cashout-row-${cashOut.id}`}>
      <View style={styles.rowHeader}>
        <MoneyDisplay cents={cashOut.amount_cents} size="sm" showCents />
        <Pill tone={badge.tone}>{badge.label}</Pill>
      </View>
      <Text variant="caption" color="textMuted">
        {formatRelative(cashOut.created_at)}
      </Text>

      {isFailed && (
        <Text variant="caption" color="textMuted">
          Couldn&apos;t send — your balance was returned.
        </Text>
      )}
    </Card>
  );
}

const styles = StyleSheet.create({
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    textAlign: 'center',
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.sm,
    color: colors.deepGreen,
  },
  titleSpacer: { width: 44 },
  center: {
    paddingVertical: spacing['2xl'],
    alignItems: 'center',
  },
  stateCard: {
    gap: spacing.md,
    alignItems: 'center',
  },
  row: {
    gap: spacing.sm,
  },
  rowHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
